import { learnerReturnPath } from 'modules/auth/return-path';
import React from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import * as AuthModule from 'modules/auth';
import * as Layouts from 'layouts';
import * as AuthPages from 'pages/Auth';
import Dashboard from 'pages/Analytics';
import History from 'pages/History';
import * as Tests from 'pages/Tests';
import * as TestConfig from 'pages/TestConfig';
import * as TestResultPages from 'pages/TestResults/index';
import * as Results from 'pages/Results';
import DocumentsPage from 'pages/Documents';
import { NotebooksPage } from 'pages/Notebooks';
import TemplatesPage from 'pages/Templates';
import * as Admin from 'pages/admin';
import * as Subjects from 'pages/Subjects';
import * as QuestionBank from 'pages/QuestionBank';
import * as Library from 'pages/Library';
import * as SectionLibrary from 'pages/SectionLibrary';
import * as Teams from 'pages/Teams';
import * as Collections from 'pages/Collections';
import * as Candidate from 'pages/Candidate';
import * as Portal from 'pages/Portal';
import { Gate } from 'components/access';
import { MobileMore } from 'components/mobile';
import { FEATURES } from 'modules/entitlements';
import { PERMISSIONS } from 'modules/auth/permissions';
import { emailVerifiedDecision, externalAccountTypeDecision, profileCompleteDecision } from 'lib/auth-guards';
import { type SettingsSection } from 'components/settings/registry';
import { LoadingScreen } from 'components/ui/LoadingScreen';
import { ProfileNameForm } from 'modules/auth/components/ProfileNameForm';
import { IS_IO } from 'lib/site';
export { LoadingScreen };
const AuthRedirect: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const location = useLocation();
    const { state: { isAuthenticated, user } } = AuthModule.Context.useAuthContext();
    const returnTo = learnerReturnPath(new URLSearchParams(location.search).get('returnTo'));
    if (isAuthenticated) {
        if (returnTo) {
            return <Navigate to={returnTo} replace/>;
        }
        if (AuthModule.isCandidateExperience(user?.accountType)) {
            return <Navigate to="/portal" replace/>;
        }
        return <Navigate to="/app" replace/>;
    }
    return <>{children}</>;
};
const RequireAuth: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const location = useLocation();
    const { state: { isAuthenticated } } = AuthModule.Context.useAuthContext();
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace/>;
    }
    return <>{children}</>;
};
// Gate only users who actually have an email. Phone-only users always have
// isEmailVerified=false; gating them would trap them at /verify-email (which
// only offers email resend) and make the onboarding screen unreachable. Decision
// logic lives in lib/auth-guards (unit-tested).
const RequireEmailVerified: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { state: { user, isFetched } } = AuthModule.Context.useAuthContext();
    const decision = emailVerifiedDecision(user, isFetched);
    if (decision.kind === 'loading') return <LoadingScreen />;
    if (decision.kind === 'redirect') return <Navigate to={decision.to} replace/>;
    return <>{children}</>;
};
// Mirrors RequireEmailVerified: new org creators (profileComplete === false) are
// redirected to the onboarding ("moderation") gate until they complete it. The
// backend onboarding/complete endpoint is the real enforcement; this is UX only.
const RequireProfileComplete: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { state: { user, isFetched } } = AuthModule.Context.useAuthContext();
    const location = useLocation();
    const returnTo = learnerReturnPath(new URLSearchParams(location.search).get('returnTo')) ?? learnerReturnPath(location.pathname + location.search);
    const decision = profileCompleteDecision(user, isFetched);
    if (decision.kind === 'loading') return <LoadingScreen />;
    if (decision.kind === 'redirect') return <Navigate to={returnTo ? `${decision.to}?returnTo=${encodeURIComponent(returnTo)}` : decision.to} replace/>;
    return <>{children}</>;
};
const RequireAccountTypeSelected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { state: { user, isFetched } } = AuthModule.Context.useAuthContext();
    const location = useLocation();
    const returnTo = learnerReturnPath(new URLSearchParams(location.search).get('returnTo')) ?? learnerReturnPath(location.pathname + location.search);
    const decision = externalAccountTypeDecision(user, isFetched);
    if (decision.kind === 'loading') return <LoadingScreen />;
    if (decision.kind === 'redirect') return <Navigate to={returnTo ? `${decision.to}?returnTo=${encodeURIComponent(returnTo)}` : decision.to} replace />;
    return <>{children}</>;
};
const RequireExternalAccountType: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { state: { user, isFetched } } = AuthModule.Context.useAuthContext();
    if (!isFetched) return <LoadingScreen />;
    if (!user?.needsAccountTypeSelection) return <Navigate to="/app" replace />;
    return <>{children}</>;
};

const RequireStaffExperience: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { state: { isFetched, user } } = AuthModule.Context.useAuthContext();
    if (!isFetched) return <LoadingScreen />;
    if (AuthModule.isCandidateExperience(user?.accountType)) return <Navigate to="/portal" replace/>;
    return <>{children}</>;
};
const RequireCandidateExperience: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { state: { isFetched, user } } = AuthModule.Context.useAuthContext();
    if (!isFetched) return <LoadingScreen />;
    if (!AuthModule.isCandidateExperience(user?.accountType)) return <Navigate to="/app" replace/>;
    return <>{children}</>;
};
// Redirects to the first page the user is actually allowed to see. This is a
// one-shot decision (replace nav on an index route), so it must wait for the
// access context to settle — permissions load fire-and-forget after isFetched
// flips, and reading them empty here would wrongly dump every staff user on
// `history` instead of `analytics`.
const AppDefaultRedirect: React.FC = () => {
    const { state: { isFetched, accessFetched, permissions } } = AuthModule.Context.useAuthContext();
    const { search } = useLocation();
    if (!isFetched || !accessFetched)
        return <LoadingScreen fullScreen={false}/>;
    if (permissions.includes(PERMISSIONS.TESTS_VIEW_RESULTS))
        return <Navigate to={{ pathname: 'analytics', search }} replace/>;
    if (permissions.includes(PERMISSIONS.TESTS_LIST))
        return <Navigate to={{ pathname: 'tests', search }} replace/>;
    // Candidates (no analytics/tests access) land on their own history.
    return <Navigate to={{ pathname: 'history', search }} replace/>;
};
// Legacy settings URLs → the settings dialog (`/app?settings=<section>`),
// preserving any extra query params (e.g. the billing return's ?topup=success).
const SettingsDialogRedirect: React.FC<{
    section: SettingsSection;
    /** Map a `:id` route param onto the dialog's `?member=` drill-down. */
    memberFromParam?: boolean;
}> = ({ section, memberFromParam }) => {
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const params = new URLSearchParams(location.search);
    params.set('settings', section);
    if (memberFromParam && id) params.set('member', id);
    return <Navigate to={{ pathname: '/app', search: `?${params.toString()}` }} replace/>;
};
// Port of the old org-settings index: admins land on the org profile,
// everyone else on their own profile.
const OrgSettingsIndexRedirect: React.FC = () => {
    const { state: { permissions: p } } = AuthModule.Context.useAuthContext();
    if (p.includes(PERMISSIONS.ROLES_EDIT)) return <SettingsDialogRedirect section="org"/>;
    return <SettingsDialogRedirect section="profile"/>;
};
export const router = createBrowserRouter([
    { path: '/', element: <RouterCatchAll /> },
    { path: '/welcome', element: <RouterCatchAll /> },
    { path: '/join', element: <AuthPages.PublicTest /> },
    { path: '/join/team/:token', element: <AuthPages.TeamJoin /> },
    { path: '/join/:code', element: <AuthPages.PublicTest /> },
    { path: '/invite/:token', element: <AuthPages.AcceptInvite /> },
    { path: '/take/:id', element: <Candidate.PublicTakeRedirect /> },
    // No-account candidate flow (chromeless, no /app shell, no auth). A public
    // test link opens the start form; starting drops into the guest take UI;
    // the result link renders the public result summary.
    {
        path: '/s/:token',
        element: <Layouts.Candidate />,
        children: [
            { index: true, element: <Candidate.GuestStart /> },
            { path: 'session/:attemptId', element: <Tests.Take guest /> },
        ],
    },
    {
        path: '/r/:resultToken',
        element: <Layouts.Candidate />,
        children: [{ index: true, element: <Candidate.GuestResult /> }],
    },
    {
        path: '/login',
        element: (<AuthRedirect>
        <AuthPages.Login />
      </AuthRedirect>),
    },
    {
        path: '/signup',
        element: (<AuthRedirect>
        <AuthPages.Signup />
      </AuthRedirect>),
    },
    {
        path: '/forgot-password',
        element: (<AuthRedirect>
        <AuthPages.ForgotPassword />
      </AuthRedirect>),
    },
    {
        // Landing point for the server-side Google OAuth redirect flow. Bare
        // route (no AuthRedirect/RequireAuth): the page reads the tokens from the
        // URL fragment, calls setTokens, then navigates itself — wrapping it would
        // race that navigation once isAuthenticated flips.
        path: '/auth/callback',
        element: <AuthPages.GoogleCallback />,
    },
    {
        path: '/verify-email',
        element: (<RequireAuth>
        <AuthPages.VerifyEmail />
      </RequireAuth>),
    },
    {
        // Onboarding gate is wrapped in RequireAuth ONLY (not the profile guard)
        // so an incomplete user can actually reach it without a redirect loop.
        path: '/onboarding',
        element: (<RequireAuth>
        <AuthPages.Onboarding />
        </RequireAuth>),
    },
    {
        path: '/account-type',
        element: (<RequireAuth>
        <RequireExternalAccountType>
        <AuthPages.AccountType />
        </RequireExternalAccountType>
        </RequireAuth>),
    },
    {
        path: '/billing/return',
        element: (<RequireAuth>
        <Admin.BillingReturn />
      </RequireAuth>),
    },
    {
        path: '/app',
        element: (<RequireAuth>
        <RequireEmailVerified>
        <RequireAccountTypeSelected>
        <RequireProfileComplete>
        <RequireStaffExperience>
        <Layouts.Main />
        </RequireStaffExperience>
        </RequireProfileComplete>
        </RequireAccountTypeSelected>
        </RequireEmailVerified>
      </RequireAuth>),
        children: [
            { index: true, element: <AppDefaultRedirect /> },
            // Mobile-only hub behind the bottom dock's "More" tab. Renders the
            // mobile menu of everything outside the primary tabs; harmless on
            // desktop (unreachable from the sidebar).
            { path: 'more', element: <MobileMore /> },
            { path: 'analytics', element: <Gate permission={PERMISSIONS.TESTS_VIEW_RESULTS}><Dashboard /></Gate> },
            { path: 'dashboard', element: <Navigate to="/app/analytics" replace/> },
            // AI Generate: the Notebooks grid, then one notebook's Sources · Chat · Studio workspace.
            { path: 'documents', element: <Gate permission={PERMISSIONS.DOCUMENTS_EDIT}><NotebooksPage /></Gate> },
            { path: 'documents/:notebookId', element: <Gate permission={PERMISSIONS.DOCUMENTS_EDIT}><DocumentsPage /></Gate> },
            { path: 'subjects', element: <Gate permission={PERMISSIONS.SUBJECTS_VIEW}><Subjects.SubjectsPage /></Gate> },
            { path: 'questions', element: <Gate permission={PERMISSIONS.QUESTIONS_VIEW}><QuestionBank.QuestionBankPage /></Gate> },
            { path: 'questions/import/review', element: <Gate permission={PERMISSIONS.QUESTIONS_CREATE}><QuestionBank.ImportReviewPage /></Gate> },
            { path: 'questions/new', element: <Gate permission={PERMISSIONS.QUESTIONS_CREATE}><QuestionBank.BankQuestionEditPage /></Gate> },
            { path: 'questions/:questionId/edit', element: <Gate permission={PERMISSIONS.QUESTIONS_EDIT}><QuestionBank.BankQuestionEditPage /></Gate> },
            { path: 'history', element: <History /> },
            // Legacy AI Learn routes fold into the AI Generate workspace.
            { path: 'learn', element: <Navigate to="/app/documents" replace /> },
            { path: 'learn/:documentId', element: <Navigate to="/app/documents" replace /> },
            { path: 'learn/:documentId/artifacts/:artifactId', element: <Navigate to="/app/documents" replace /> },
            { path: 'library', element: <Gate feature={FEATURES.COLLECTIONS} permission={PERMISSIONS.COLLECTIONS_LIST}><Library.LibraryPage /></Gate> },
            // P10 section library; the page itself shows the upgrade path when neither suite is on the plan.
            { path: 'section-library', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><SectionLibrary.SectionLibraryPage /></Gate> },
            { path: 'section-library/:id', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><SectionLibrary.SectionBlockPage /></Gate> },
            { path: 'collections/:id', element: <Gate feature={FEATURES.COLLECTIONS} permission={PERMISSIONS.COLLECTIONS_VIEW}><Collections.CollectionDetailPage /></Gate> },
            { path: 'teams', element: <Gate feature={FEATURES.TEAMS} permission={PERMISSIONS.TEAMS_LIST}><Teams.TeamsListPage /></Gate> },
            { path: 'teams/:teamId', element: <Gate feature={FEATURES.TEAMS} permission={PERMISSIONS.TEAMS_VIEW}><Teams.TeamDetailPage /></Gate> },
            { path: 'teams/:teamId/members', element: <Gate feature={FEATURES.TEAMS} permission={PERMISSIONS.TEAMS_VIEW}><Teams.TeamMembersPage /></Gate> },
            { path: 'teams/:teamId/collections', element: <Gate feature={FEATURES.TEAMS} permission={PERMISSIONS.TEAMS_VIEW}><Teams.TeamCollectionsPage /></Gate> },
            { path: 'teams/:teamId/settings', element: <Gate feature={FEATURES.TEAMS} permission={PERMISSIONS.TEAMS_EDIT}><Teams.TeamSettingsPage /></Gate> },
            {
                path: 'tests',
                element: <Gate permission={PERMISSIONS.TESTS_LIST}><Layouts.TestsLayout /></Gate>,
                children: [
                    { index: true, element: <Tests.List /> },
                    { path: 'create', element: <Gate permission={PERMISSIONS.TESTS_CREATE}><TestConfig.CreateTestRedirect /></Gate> },
                    { path: 'templates', element: <Gate permission={PERMISSIONS.TESTS_CREATE}><TemplatesPage /></Gate> },
                    {
                        path: ':testId',
                        element: <Layouts.TestScopedContent />,
                        children: [
                            { index: true, element: <Gate permission={PERMISSIONS.TESTS_EDIT}><TestConfig.OverviewHubPage /></Gate> },
                            { path: 'settings', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><TestConfig.BasicSettingsPage /></Gate> },
                            { path: 'questions', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><TestConfig.QuestionsTab /></Gate> },
                            { path: 'access', element: <Gate permission={PERMISSIONS.ACCESS_KEYS_CREATE}><TestConfig.TestAccessPage /></Gate> },
                            { path: 'grading', element: <Gate permission={PERMISSIONS.GRADING_GRADE}><TestConfig.GradingSummaryPage /></Gate> },
                            { path: 'reviews', element: <Gate permission={[PERMISSIONS.GRADING_VIEW, PERMISSIONS.GRADING_GRADE]}><Results.TestReviews /></Gate> },
                            { path: 'time', element: <TestConfig.SettingsRedirect hash="timing" /> },
                            { path: 'certificate', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><TestConfig.CertificateTemplatePage /></Gate> },
                            { path: 'anti-cheating', element: <TestConfig.SettingsRedirect hash="anticheat" /> },
                            { path: 'gamification', element: <TestConfig.SettingsRedirect hash="gamification" /> },
                            { path: 'worksheet', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><TestConfig.WorksheetPage /></Gate> },
                            { path: 'monitoring', element: <Gate permission={PERMISSIONS.MONITORING_MONITOR}><TestResultPages.RespondentMonitoringPage /></Gate> },
                            { path: 'results', element: <Gate permission={PERMISSIONS.TESTS_VIEW_RESULTS}><TestResultPages.ResultsTablePage /></Gate> },
                            { path: 'sheets-review', element: <Gate permission={PERMISSIONS.TESTS_VIEW_RESULTS}><TestResultPages.SheetsReviewPage /></Gate> },
                            { path: 'answers-review', element: <Gate permission={PERMISSIONS.TESTS_VIEW_RESULTS}><TestResultPages.AnswersReviewPage /></Gate> },
                            { path: 'statistics', element: <Gate permission={PERMISSIONS.TESTS_VIEW_RESULTS}><TestResultPages.StatisticsPage /></Gate> },
                            { path: 'feedback', element: <Gate permission={PERMISSIONS.TESTS_VIEW_RESULTS}><TestResultPages.FeedbackPage /></Gate> },
                            { path: 'question-reports', element: <Gate permission={PERMISSIONS.TESTS_VIEW_RESULTS}><TestResultPages.QuestionReportsPage /></Gate> },
                            { path: 'unused-codes', element: <Gate permission={PERMISSIONS.ACCESS_KEYS_CREATE}><TestResultPages.UnusedCodesPage /></Gate> },
                        ],
                    },
                ],
            },
            { path: 'tests/edit/:id', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><Tests.EditRedirect /></Gate> },
            { path: 'tests/preview/:id', element: <Gate permission={PERMISSIONS.TESTS_EDIT}><Tests.Take /></Gate> },
            { path: 'tests/take/:id', element: <Tests.Take /> },
            { path: 'tests/take/:id/sessions/:attemptId', element: <Tests.Take /> },
            { path: 'results/:id', element: <Results.Single /> },
            { path: 'grading', element: <Gate permission={PERMISSIONS.GRADING_GRADE}><Results.ManualGrading /></Gate> },
            { path: 'invites', element: <Navigate to="/app/admin/organization/access-keys" replace/> },
            // Settings live in the app-wide dialog (?settings=<section>);
            // legacy URLs redirect into it, preserving extra query params.
            { path: 'settings', element: <SettingsDialogRedirect section="org-preferences"/> },
            { path: 'account', element: <SettingsDialogRedirect section="profile"/> },
            { path: 'admin/users', element: <Navigate to="/app/admin/organization/users" replace/> },
            { path: 'admin/users/:id', element: <AdminUserDetailsRedirect /> },
            {
                path: 'admin/organization',
                children: [
                    { index: true, element: <OrgSettingsIndexRedirect /> },
                    { path: 'profile', element: <SettingsDialogRedirect section="profile"/> },
                    { path: 'settings', element: <SettingsDialogRedirect section="org"/> },
                    { path: 'preferences', element: <SettingsDialogRedirect section="org-preferences"/> },
                    { path: 'notifications', element: <SettingsDialogRedirect section="org-notifications"/> },
                    { path: 'billing', element: <SettingsDialogRedirect section="billing"/> },
                    { path: 'users', element: <SettingsDialogRedirect section="members"/> },
                    { path: 'users/:id', element: <SettingsDialogRedirect section="members" memberFromParam/> },
                    { path: 'permissions', element: <SettingsDialogRedirect section="roles"/> },
                    { path: 'access-keys', element: <SettingsDialogRedirect section="access-keys"/> },
                ],
            },
            { path: 'admin/transactions', element: <Navigate to="/app/admin/organization/billing" replace/> },
            { path: 'admin/permissions', element: <Navigate to="/app/admin/organization/permissions" replace/> },
            { path: '*', element: <Navigate to="/app" replace/> },
        ],
    },
    {
        // Candidate portal (Wayground-style student account). Only CANDIDATE-role
        // users land here; staff are redirected to /app. The guards wrap a bare
        // Outlet so children can pick their own shell: the browse pages use the
        // top-bar CandidatePortal layout, while the take-test and result flows use
        // a chromeless layout (no nav to wander off into mid-attempt).
        path: '/portal',
        element: (<RequireAuth>
        <RequireEmailVerified>
        <RequireAccountTypeSelected>
        <RequireProfileComplete>
        <RequireCandidateExperience>
        <Outlet />
        </RequireCandidateExperience>
        </RequireProfileComplete>
        </RequireAccountTypeSelected>
        </RequireEmailVerified>
      </RequireAuth>),
        children: [
            {
                element: <Layouts.CandidatePortal />,
                children: [
                    // examing.uz keeps only the learner marketplace: no groups
                    // in the portal. Old /portal/groups links fall to the '*' redirect.
                    { index: true, element: <Portal.PortalHome /> },
                    ...(IS_IO ? [] : [{ path: 'tests', element: <Portal.TestsCatalogRoute /> }]),
                    ...(IS_IO ? [] : [{ path: 'leaderboard', element: <Portal.Leaderboard /> }]),
                    { path: 'settings', element: <Portal.Settings />, children: [
                        { index: true, element: <ProfileNameForm /> },
                        // Learner plans and referrals are examing.uz only.
                        ...(IS_IO ? [] : [
                            { path: 'subscription', element: <Portal.Subscription /> },
                            { path: 'plan', element: <Portal.StudyPlan /> },
                            { path: 'referrals', element: <Portal.Referrals /> },
                        ]),
                        { path: 'history', element: <Portal.Results /> },
                        // Xatolarim moved to the top bar; old links still land on it.
                        ...(IS_IO ? [] : [{ path: 'mistakes', element: <Navigate to="/portal/mistakes" replace /> }]),
                    ] },
                    ...(IS_IO ? [] : [{ path: 'mistakes', element: <Portal.Mistakes /> }]),
                    // IELTS/SAT vocabulary saved from passages (examing.uz only).
                    ...(IS_IO ? [] : [
                        { path: 'words', element: <Portal.Words /> },
                        { path: 'words/cards', element: <Portal.Flashcards /> },
                    ]),
                    { path: 'results', element: <Navigate to="/portal/settings/history" replace /> },
                ],
            },
            {
                // Focused, chromeless flows — the candidate's own take-test and
                // single-result views, mirroring the /app routes but sealed inside
                // the portal so they never touch the staff shell.
                element: <Layouts.Candidate />,
                children: [
                    { path: 'tests/take/:id', element: <Tests.Take /> },
                    { path: 'tests/take/:id/sessions/:attemptId', element: <Tests.Take /> },
                    { path: 'results/:id', element: <Results.Single /> },
                ],
            },
            { path: '*', element: <Navigate to="/portal" replace/> },
        ],
    },
    {
        path: '*',
        element: <RouterCatchAll />,
    },
]);
function RouterCatchAll() {
    const { state: { isAuthenticated } } = AuthModule.Context.useAuthContext();
    return isAuthenticated ? (<Navigate to="/app" replace/>) : (<Navigate to="/login" replace/>);
}
function AdminUserDetailsRedirect() {
    const { id } = useParams<{
        id: string;
    }>();
    if (!id)
        return <Navigate to="/app/admin/organization/users" replace/>;
    return <Navigate to={`/app/admin/organization/users/${id}`} replace/>;
}
